import { DeleteOutlined, RightOutlined } from "@ant-design/icons";
import {
    Button,
    Card,
    Dropdown,
    Image,
    Menu,
    Popconfirm,
    Space,
    Tag,
    Typography
} from "antd";
import { useRef, useState } from "react";
import "./VideoRow.scss";

const { Paragraph } = Typography;

const VideoRow = ({
    index = -1,
    module,
    removeable = false,
    removeFromVideoBank,
    assignableModules = [],
    onAssignToModule,
    video,
    updateTempState,
    isDisabled = false
}) => {
    const [vimeoThumbnailUrl, setVimeoThumbnailUrl] = useState(null);

    const fetchVimeoThumbnail = async (vid) => {
        const response = await fetch(
            `https://vimeo.com/api/v2/video/${vid.embedId}.json`
        );
        const json = await response.json();
        return setVimeoThumbnailUrl(json[0]["thumbnail_large"]);
    };

    const getVideoThumbnail = (vid) => {
        if (vid.videoType === "youtube") {
            return (
                <Image
                    src={`https://img.youtube.com/vi/${vid.embedId}/mqdefault.jpg`}
                    preview={false}
                    style={{ aspectRatio: 16 / 9 }}
                />
            );
        } else if (vid.videoType === "vimeo") {
            fetchVimeoThumbnail(vid);
            return (
                <Image
                    src={vimeoThumbnailUrl}
                    preview={false}
                    style={{ aspectRatio: 16 / 9 }}
                />
            );
        }
        return null;
    };

    const renderVideoTypeTag = (videoType) => {
        if (videoType === "youtube") {
            return <Tag color="#cd201f">YouTube</Tag>;
        } else if (videoType === "vimeo") {
            return <Tag color="#00adef">Vimeo</Tag>;
        }
        return null;
    };

    const renderBorrowPrice = () => {
        if (
            (module && module.id !== video.parentModule) ||
            (!module && video.parentModule)
        ) {
            return (
                <Tag color="orange">+{video.parentModulePrice} cupoints</Tag>
            );
        }
        return null;
    };

    const handleAssign = ({ key }) => {
        if (!onAssignToModule) return;
        onAssignToModule(key, video);
    };

    const assignMenu =
        assignableModules?.length > 0 && !isDisabled ? (
            <Menu
                className="video-row-assign-menu"
                data-no-drag
                onClick={handleAssign}
                items={assignableModules.map((m) => ({
                    key: `${m.key ?? m.id ?? m.name}`,
                    label: m.name || m.title || "Module"
                }))}
            />
        ) : null;

    const dropdownRef = useRef(null);
    const [dropdownPlacement, setDropdownPlacement] = useState("bottomRight");

    const handleDropdownVisibleChange = (visible) => {
        if (!visible) return;
        const node = dropdownRef.current;
        if (!node) return;

        const rect = node.getBoundingClientRect();
        const viewportHeight =
            window.innerHeight || document.documentElement.clientHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const estimatedMenuHeight = Math.min(
            260,
            (assignableModules?.length || 1) * 36 + 24
        );

        setDropdownPlacement(
            spaceBelow < estimatedMenuHeight ? "topRight" : "bottomRight"
        );
    };

    const cover = (
        <div className="video-row-cover">
            {getVideoThumbnail(video)}
            <div className="video-row-tags">
                <Space size={0}>
                    {renderVideoTypeTag(video.videoType)}
                    {renderBorrowPrice()}
                </Space>
            </div>
        </div>
    );

    return (
        <Card cover={cover}>
            <Space direction="vertical" style={{ width: "100%" }} size={0}>
                <Paragraph
                    ellipsis={{ rows: 1, tooltip: true }}
                    data-no-drag
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    editable={
                        isDisabled
                            ? false
                            : {
                                autoSize: { maxRows: 1 },
                                onChange: updateTempState,
                                onStart: (e) => {
                                    if (e?.stopPropagation) e.stopPropagation();
                                }
                            }
                    }>
                    {video.title}
                </Paragraph>

                <div
                    data-no-drag
                    className="video-row-actions"
                    ref={dropdownRef}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}>
                    <Space align="end" size="small">
                        {removeable && !isDisabled ? (
                            <Popconfirm
                                title="Are you sure to remove this video?"
                                onConfirm={() => removeFromVideoBank(index)}
                                onCancel={(e) => {
                                    e?.stopPropagation();
                                }}
                                okText="Yes"
                                cancelText="No"
                                okButtonProps={{
                                    "data-no-drag": true
                                }}
                                cancelButtonProps={{
                                    "data-no-drag": true
                                }}>
                                <Button
                                    icon={<DeleteOutlined />}
                                    danger
                                    data-no-drag
                                    size="small"
                                />
                            </Popconfirm>
                        ) : null}

                        {assignMenu && !isDisabled ? (
                            <Dropdown
                                size="small"
                                overlay={assignMenu}
                                autoAdjustOverflow
                                placement={dropdownPlacement}
                                onVisibleChange={handleDropdownVisibleChange}
                                onClick={(e) => e.preventDefault()}>
                                <Button size="small">
                                    <Space>
                                        <span>Assign To</span>
                                        <RightOutlined />
                                    </Space>
                                </Button>
                            </Dropdown>
                        ) : null}
                    </Space>
                </div>
            </Space>
        </Card>
    );
};

export default VideoRow;
